import React from "react";
import { Phone, Mail, Info } from "lucide-react";
import Modal from "./Modal";

interface ContactMember {
  email: string;
  name?: string;
}

interface OrganisationInfoModalProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  organizationName: string;
  contactPhones: string[];
  contactEmails: string[];
  members: ContactMember[];
}

const OrganisationInfoModal: React.FC<OrganisationInfoModalProps> = ({
  isOpen,
  setIsOpen,
  organizationName,
  contactPhones,
  contactEmails,
  members,
}) => {
  const hasContactInfo = contactPhones.length > 0 || contactEmails.length > 0;

  return (
    <Modal isOpen={isOpen} setIsOpen={setIsOpen}>
      <div className="flex items-center gap-3 mb-4">
        <div className="bg-[#1d365d]/10 text-[#1d365d] p-2 rounded-lg">
          <Info size={20} />
        </div>
        <h3 className="text-lg font-semibold text-gray-900">
          {organizationName}
        </h3>
      </div>

      <p className="text-sm text-gray-500 mb-5">
        If you need assistance or have questions about this organisation, you can
        reach out using the contact details below.
      </p>

      {!hasContactInfo ? (
        <p className="text-sm text-gray-400 italic">
          No contact information has been added yet.
        </p>
      ) : (
        <div className="space-y-3">
          {contactPhones.length > 0 && (
            <div className="space-y-1.5">
              {contactPhones.map((phone, index) => (
                <div key={index} className="flex items-center gap-2.5">
                  <Phone size={14} className="text-gray-400 flex-shrink-0" />
                  <a
                    href={`tel:${phone}`}
                    className="text-sm text-gray-700 hover:text-[#1d365d] transition-colors"
                  >
                    {phone}
                  </a>
                </div>
              ))}
            </div>
          )}

          {contactEmails.length > 0 && (
            <div className="space-y-1.5">
              {contactEmails.map((email, index) => {
                const member = members.find((m) => m.email === email);
                const name = member?.name && member.name !== email ? member.name : null;
                return (
                  <div key={index} className="flex items-center gap-2.5">
                    <Mail size={14} className="text-gray-400 flex-shrink-0" />
                    <div className="flex items-center gap-1.5">
                      <a
                        href={`mailto:${email}`}
                        className="text-sm text-blue-600 hover:text-blue-700 transition-colors"
                      >
                        {email}
                      </a>
                      {name && (
                        <span className="text-sm text-gray-400">({name})</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

export default OrganisationInfoModal;
